import { defineStore } from 'pinia';
import api from '@/api/api';
import applicationAPI from '@/api/application';
import {type ZApplication, type ZApplicant, type ZQueryResultApplications} from '@schemas/application.schemas';
import type {Id, QueryResult} from "@typesDef/general.types";
import {setIdsUnique} from "@/stores/helpers/storeHelper";

export const useApplicationsStore = defineStore('applications', {
    state: () => ({
        applicationsById: {} as Record<Id, ZApplication>,
        // all applications that are related to the current/my user
        myApplicationIds: [] as Id[],
        currentApplicationId: null as Id | null,

        // All applications for the current event eventId -> { count, results<Id> },
        eventApplications: {} as Record<Id, QueryResult<Id>>,

        // all applicants for an eventId (used in rating event applicants)
        // here we store the full ZApplicant obj instead of having something with applicantsById structure
        eventApplicants: {} as Record<Id, ZApplicant[]>
    }),
    getters: {
        myApplications(state): ZApplication[] {
            return state.myApplicationIds.map(id => state.applicationsById[id]).filter(Boolean) as ZApplication[];
        },
        currentApplication(state): ZApplication | null {
            return state.currentApplicationId ? state.applicationsById[state.currentApplicationId] ?? null : null;
        },
        getApplicantsForEvent: (state) => (eventId: string): ZApplicant[] => {
            return state.eventApplicants[eventId] || [];
        },
        // only used in this store for caching in fetchMyApplicationsByEventId
        getApplicationsForEvent: (state) => (eventId: string): ZQueryResultApplications => {
            // we filter the applications for the event from the applicationsById map,
            // since we want to have them in the cache and also to avoid making multiple requests to the backend
            // when we already have the data
            return {
                count: state.eventApplications[eventId]?.count || 0,
                results: state.eventApplications[eventId]?.results
                    .map(id => state.applicationsById[id])
                    .filter(Boolean) as ZApplication[] || []
            }
        },

    },
    actions: {
        clearMyData(): void {
            this.applicationsById = {};
            this.myApplicationIds = [];
            this.currentApplicationId = null;

            this.eventApplications = {};
            this.eventApplicants = {};
        },

        // FIXME move them to general library since they are used in multiple stores
        upsertMany(docs: ZApplication[]) {
            for (const e of docs) this.applicationsById[e._id] = e
        },

        /**
         * Fetches an application by its ID. The fetched application is added to the "applicationsById" map and the "currentApplicationId" is set to the fetched application's ID.
         *
         */
        async fetchApplication(applicationId: Id): Promise<ZApplication> {
            log.debug(`fetchApplication(${applicationId})`)
            const cached = this.applicationsById[applicationId];
            if(cached) {
                this.currentApplicationId = applicationId;
                return cached;
            }
            const doc = await api.getDocument<ZApplication>('applications', applicationId);
            this.applicationsById[doc._id] = doc;
            this.currentApplicationId = doc._id;

            return doc;
        },

        /**
         * Fetches all applications for the current user.
         * The fetched applications are added to the "applicationsById" map
         * and their IDs are added to the "myApplicationIds" array.
         * @param query
         * @returns {Promise<ZApplication[]>}
         */
        async fetchMyApplications(query: Record<string, any> = {}): Promise<ZApplication[]> {
            const docs = await api.getDocuments<ZApplication>('applications', query);
            this.upsertMany(docs);
            setIdsUnique( this.myApplicationIds, docs.map(d => d._id));
            return docs;
        },
        /**
         * Fetch applications for the current event
         * @param eventId
         * @param query
         */
        async fetchMyApplicationsByEventId(eventId: string, query: Record<string, any> = {}): Promise<ZQueryResultApplications> {
            log.debug(`fetchMyApplicationsByEventId(${eventId}) query=${JSON.stringify(query)})`);
            const docs = await api.getDocumentsWithCount<ZApplication>('applications/event/' + eventId, query);
            if(docs && docs.results) {
                // updateById list
                this.upsertMany(docs.results);
                const IdList = docs.results.map(d => d._id);
                // update myApplicationIds list
                setIdsUnique(this.myApplicationIds, IdList);

                // update eventApplications map
                if(this.eventApplications[eventId]) {
                    // if we already have applications for the event in the cache, we need to update the count and the results list, since we might have new applications in the backend that are not in the cache
                    const existingIds = this.eventApplications[eventId].results;
                    setIdsUnique(existingIds, IdList);

                    this.eventApplications[eventId].count = this.eventApplications[eventId].results.length;
                } else {
                    // if we do not have applications for the event in the cache, we set the count and the results list directly
                    this.eventApplications[eventId] = {
                        count: docs.count,
                        results: IdList
                    };
                }
            }
            return docs;

            /*
            const found = this.getApplicationsForEvent(eventId);
            log.debug(`found applications: ${found?.length}`);

            // we check if we have applications for the event in the cache, if yes we use them and set the eventApplications,
            // the issue is if we have let´s say 9 applications and in between we get newer applications and we fetch again with a
            // limit of 10, we will have 10 applications in the backend but only 9 in the cache, so we will fetch again from the backend and update the cache, but this is a tradeoff we have to make to avoid making multiple requests to the backend when we already have the data in the cache

            if (found.length > 0) {
                this.eventApplications = {
                    results: found,
                    count: found.length
                };
            } else {
                this.eventApplications = await api.getDocumentsWithCount<ZApplication>('applications/event/' + eventId, query);
                // Optionally push to `this.applications` to cache:

                // this.applications.push(...this.eventApplications);
            }
            return this.eventApplications;
            */
        },
        /**
         * Fetch applicants for a specific event. The fetched applicants are added to the "eventApplicants" map with the event ID as the key.
         * @param eventId The ID of the event to fetch applicants for
         * @return The list of applicants for the specified event
         */
        async fetchApplicantsByEvent(eventId: string): Promise<ZApplicant[]> {
            log.debug(`fetchApplicantsByEvent(${eventId})`);
            if(this.eventApplicants[eventId]) {
                log.debug(`found applicants for event ${eventId} in cache: ${this.eventApplicants[eventId].length}`);
                return this.eventApplicants[eventId];
            }
            const docs = await api.getDocuments<ZApplicant>('applications/event/' + eventId + '/applicants');
            this.eventApplicants[eventId] = docs;
            return docs;
        },

        /**
         * Create a new application for an event.
         * The created application is added to the "applicationsById" map and
         * its ID is added to the "myApplicationIds" list since it's owned by the user.
         * The "currentApplicationId" is set to the created application's ID.
         * @param eventId The ID of the event to apply for
         * @param applicants The applicants for the application
         * @param message An optional message to include with the application
         * @return The created application
         */
        async createApplication(eventId: string, applicants: ZApplicant[], message: string): Promise<ZApplication> {
            const created = await applicationAPI.apply(eventId, applicants, message);
            const newId = created?._id;
            if(!newId) {
                throw new Error('Failed to create application');
            }
            this.applicationsById [newId] = created;
            // add to myApplicationIds if not already there
            if(!this.myApplicationIds.includes(newId)) {
                this.myApplicationIds.unshift(newId); // we want to see our new application at the top of the list, so unshift instead of push
            }
            this.currentApplicationId = newId;
            return created;
        },
        /**
         * Update the status of an application. The updated application is added to the "applicationsById" map and
         * replaces the existing application with the same ID. If the updated application is the current application,
         * the "currentApplicationId" is updated to the updated application's ID.
         * @param applicationId The ID of the application to update
         * @param status The new status of the application
         * @param message An optional message to include with the status update
         * @return The updated application
         */
        async updateStatus(applicationId: string, status: string, message: string = ""): Promise<ZApplication> {
            const updated = await applicationAPI.updateStatus(applicationId, status, message);
            if(!updated || !updated._id) {
                throw new Error('Failed to update application');
            }
            this.applicationsById[updated._id] = updated;
            return updated;
        },

        /**
         * Delete an application by its ID.
         * The deleted application is removed from the "applicationsById" map and
         * its ID is removed from the "myApplicationIds" list.
         * If the deleted application is the current application, the "currentApplicationId" is set to null.
         * @param applicationId The ID of the application to delete
         * @return A promise that resolves when the application has been deleted
         */
        async removeApplication(applicationId: Id): Promise<void> {
            await api.deleteDocument<ZApplication>('applications', applicationId)
            delete this.applicationsById[applicationId];
            this.myApplicationIds = this.myApplicationIds.filter(id => id !== applicationId);
            if(this.currentApplicationId === applicationId) {
                this.currentApplicationId = null;
            }

            // get rid of the application from eventApplications if it exists there
            for (const [eventId, apps] of Object.entries(this.eventApplications)) {
                if (apps.results.includes(applicationId)) {
                    apps.results = apps.results.filter(id => id !== applicationId);
                    apps.count = Math.max(0, apps.count - 1);
                }
            }
        }
    }
});
